import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Loader2, AlertCircle, Globe, RefreshCw, CheckCircle2, Edit3 } from 'lucide-react';

const FALLBACK_ERROR_TYPES = ['paywall', 'auth_required', 'blocked'] as const;

interface IExtractionState {
  status: 'idle' | 'extracting' | 'success' | 'error';
  extractedText: string;
  extractedTitle?: string;
  error?: string;
  errorType?: string;
  canRetry: boolean;
}

interface IWritingStyleUrlExtractionProps {
  sampleUrl: string;
  onUrlChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  extraction: IExtractionState;
  onExtractText: () => void;
  onExtractedTextChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  isEditingExtractedText: boolean;
  onToggleEditExtractedText: () => void;
  isSubmitting: boolean;
  error?: string;
  extractedWordCount: number;
}

export function WritingStyleUrlExtraction({
  sampleUrl,
  onUrlChange,
  extraction,
  onExtractText,
  onExtractedTextChange,
  isEditingExtractedText,
  onToggleEditExtractedText,
  isSubmitting,
  error,
  extractedWordCount,
}: IWritingStyleUrlExtractionProps) {
  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="sampleUrl">Article URL *</Label>
        <div className="flex gap-2">
          <Input
            id="sampleUrl"
            type="url"
            placeholder="https://example.com/article"
            value={sampleUrl}
            onChange={onUrlChange}
            disabled={isSubmitting || extraction.status === 'extracting'}
            className={`flex-1 ${error ? 'border-destructive' : ''}`}
          />
          <Button
            type="button"
            variant="secondary"
            onClick={onExtractText}
            disabled={isSubmitting || extraction.status === 'extracting' || !sampleUrl.trim()}
          >
            {extraction.status === 'extracting' ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Extracting...
              </>
            ) : extraction.canRetry ? (
              <>
                <RefreshCw className="h-4 w-4 mr-2" />
                Retry
              </>
            ) : (
              <>
                <Globe className="h-4 w-4 mr-2" />
                Extract
              </>
            )}
          </Button>
        </div>
        {error && (
          <p className="text-sm text-destructive flex items-center gap-1">
            <AlertCircle className="h-3 w-3" />
            {error}
          </p>
        )}
      </div>

      {extraction.status === 'extracting' && (
        <div className="bg-muted/50 p-4 rounded-lg flex items-center gap-3">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          <div>
            <p className="font-medium">Extracting article content...</p>
            <p className="text-sm text-muted-foreground">This may take a few seconds</p>
          </div>
        </div>
      )}

      {extraction.status === 'error' && (
        <div
          className={`p-4 rounded-lg ${
            extraction.canRetry
              ? 'bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800'
              : 'bg-destructive/10 border border-destructive/20'
          }`}
        >
          <div className="flex items-start gap-3">
            <AlertCircle
              className={`h-5 w-5 mt-0.5 ${extraction.canRetry ? 'text-yellow-600' : 'text-destructive'}`}
            />
            <div className="flex-1">
              <p
                className={`font-medium ${extraction.canRetry ? 'text-yellow-700 dark:text-yellow-400' : 'text-destructive'}`}
              >
                {extraction.canRetry ? 'Temporary Error' : 'Extraction Failed'}
              </p>
              <p className="text-sm mt-1">{extraction.error}</p>
              {(FALLBACK_ERROR_TYPES as readonly string[]).includes(extraction.errorType || '') && (
                <p className="text-sm mt-2 text-muted-foreground">
                  Switching to manual text input in 3 seconds...
                </p>
              )}
              {extraction.canRetry && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="mt-2"
                  onClick={onExtractText}
                >
                  <RefreshCw className="h-3 w-3 mr-2" />
                  Try Again
                </Button>
              )}
            </div>
          </div>
        </div>
      )}

      {(extraction.status === 'success' ||
        (extraction.status === 'error' && extraction.extractedText)) && (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label htmlFor="extractedText" className="flex items-center gap-2">
              {extraction.status === 'success' && (
                <CheckCircle2 className="h-4 w-4 text-green-600" />
              )}
              Extracted Text
              {extraction.extractedTitle && (
                <span className="text-muted-foreground font-normal">
                  — {extraction.extractedTitle}
                </span>
              )}
            </Label>
            <Button type="button" variant="ghost" size="sm" onClick={onToggleEditExtractedText}>
              <Edit3 className="h-3 w-3 mr-1" />
              {isEditingExtractedText ? 'Done' : 'Edit'}
            </Button>
          </div>
          <Textarea
            id="extractedText"
            value={extraction.extractedText}
            onChange={onExtractedTextChange}
            disabled={!isEditingExtractedText}
            className={`min-h-[150px] ${
              !isEditingExtractedText ? 'bg-muted/50 cursor-default' : ''
            } ${extractedWordCount < 300 ? 'border-yellow-500' : ''}`}
          />
          <div className="flex items-center justify-between text-sm">
            <span className={`${extractedWordCount >= 300 ? 'text-green-600' : 'text-yellow-600'}`}>
              Word count: {extractedWordCount} / 300
              {extractedWordCount >= 300 && extractedWordCount <= 500 && ' (adequate)'}
              {extractedWordCount > 500 && ' (excellent!)'}
            </span>
            {extractedWordCount < 300 && (
              <span className="text-yellow-600 flex items-center gap-1">
                <AlertCircle className="h-3 w-3" />
                Add more text or use manual input
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
